if (!customElements.get('monoree-card-gallery')) {
  customElements.define('monoree-card-gallery', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.viewport = this.querySelector('.monoree-card-gallery__viewport');
      this.slides = [...this.querySelectorAll('.monoree-card-gallery__slide')];
      if (!this.viewport || this.slides.length < 2) return;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.dots = [...this.querySelectorAll('[data-index]')];
      this.arrows = [...this.querySelectorAll('[data-step]')];
      this.status = this.querySelector('.monoree-card-gallery__status');
      this.statusTemplate = this.dataset.status;
      this.index = 0;
      this.targetIndex = 0;
      this.blockClickUntil = 0;
      this.addEventListener('click', event => this.onClick(event), { capture: true, signal });
      this.addEventListener('keydown', event => this.onKeyDown(event), { signal });
      this.viewport.addEventListener('pointerdown', event => {
        if (!event.isPrimary || event.button !== 0) return;
        this.pointer = { id: event.pointerId, x: event.clientX, y: event.clientY, moved: false };
        this.programmatic = false;
        this.targetIndex = this.index;
        this.blockClickUntil = 0;
      }, { passive: true, signal });
      this.viewport.addEventListener('pointermove', event => {
        if (!this.pointer || this.pointer.id !== event.pointerId) return;
        const dx = event.clientX - this.pointer.x;
        const dy = event.clientY - this.pointer.y;
        if (Math.hypot(dx, dy) > 10) this.pointer.moved = true;
        if (Math.abs(dx) > 8 && Math.abs(dx) > Math.abs(dy)) this.load(this.index + (dx < 0 ? 1 : -1));
      }, { passive: true, signal });
      this.viewport.addEventListener('pointerup', () => this.endPointer(false), { passive: true, signal });
      this.viewport.addEventListener('pointercancel', () => this.endPointer(true), { passive: true, signal });
      this.viewport.addEventListener('dragstart', event => event.preventDefault(), { signal });
      this.viewport.addEventListener('scroll', () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = 0;
          const index = Math.round(this.viewport.scrollLeft / this.viewport.clientWidth);
          if (Number.isFinite(index)) this.update(index);
          if (!this.programmatic) this.targetIndex = this.index;
        });
        clearTimeout(this.settleTimer);
        this.settleTimer = setTimeout(() => this.settle(), 150);
      }, { passive: true, signal });
      this.viewport.addEventListener('scrollend', () => this.settle(), { signal });
      this.imageObserver = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting && entry.intersectionRatio > 0) this.load(this.slides.indexOf(entry.target));
        });
      }, { root: this.viewport, threshold: .01 });
      this.slides.forEach(slide => this.imageObserver.observe(slide));
      this.width = this.viewport.clientWidth;
      this.resizeObserver = new ResizeObserver(() => {
        const width = this.viewport.clientWidth;
        if (!width || width === this.width) return;
        this.width = width;
        this.viewport.scrollTo({ left: this.index * width, behavior: 'instant' });
      });
      this.resizeObserver.observe(this.viewport);
      this.classList.add('is-ready');
      this.update(0);
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.imageObserver?.disconnect();
      this.resizeObserver?.disconnect();
      cancelAnimationFrame(this.frame);
      clearTimeout(this.announceTimer);
      clearTimeout(this.settleTimer);
      this.frame = 0;
      this.classList.remove('is-ready');
    }
    load(index) {
      const slide = this.slides[index];
      const template = slide?.querySelector('template');
      if (!template) return;
      slide.append(template.content.cloneNode(true));
      template.remove();
    }
    endPointer(cancelled) {
      if (cancelled || this.pointer?.moved) this.blockClickUntil = performance.now() + 600;
      this.pointer = null;
    }
    onClick(event) {
      const button = event.target.closest('button');
      if (button && this.contains(button)) {
        event.preventDefault();
        event.stopPropagation();
        if (button.dataset.index !== undefined) this.go(Number(button.dataset.index));
        else if (button.dataset.step) this.go(this.targetIndex + Number(button.dataset.step));
        return;
      }
      if (event.target.closest('a') && performance.now() < this.blockClickUntil) {
        event.preventDefault();
        event.stopPropagation();
      }
    }
    onKeyDown(event) {
      if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
      const key = event.key;
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(key)) return;
      event.preventDefault();
      event.stopPropagation();
      const index = key === 'Home' ? 0 : key === 'End' ? this.slides.length - 1 : this.targetIndex + (key === 'ArrowRight' ? 1 : -1);
      const imageFocused = this.slides.includes(document.activeElement);
      this.go(index);
      if (imageFocused) this.slides[this.targetIndex].focus({ preventScroll: true });
    }
    go(index) {
      this.targetIndex = Math.max(0, Math.min(this.slides.length - 1, index));
      this.programmatic = true;
      this.load(this.targetIndex);
      this.viewport.scrollTo({
        left: this.targetIndex * this.viewport.clientWidth,
        behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'
      });
    }
    settle() {
      this.programmatic = false;
      this.targetIndex = this.index;
    }
    update(index) {
      this.index = Math.max(0, Math.min(this.slides.length - 1, index));
      this.load(this.index);
      this.slides.forEach((slide, i) => {
        slide.tabIndex = i === this.index ? 0 : -1;
        if (i === this.index) slide.removeAttribute('aria-hidden');
        else slide.setAttribute('aria-hidden', 'true');
      });
      this.dots.forEach((dot, i) => {
        if (i === this.index) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
      this.arrows.forEach(button => {
        const unavailable = Number(button.dataset.step) < 0 ? this.index === 0 : this.index === this.slides.length - 1;
        button.setAttribute('aria-disabled', String(unavailable));
      });
      clearTimeout(this.announceTimer);
      this.announceTimer = setTimeout(() => {
        this.status.textContent = this.statusTemplate.replace('[index]', String(this.index + 1));
      }, 150);
    }
  });
}
